// JobSchedule.js
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

// ** MUI Components
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Grid,
  FormLabel,
  FormHelperText,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import { useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { MobileTimePicker } from "@mui/x-date-pickers-pro";

import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Import
import CustomTextField from "components/mui/text-field";
import CustomChip from "components/mui/chip";

// ** API helper
// import { saveWorkRequirementSchedule } from "screens/WorkOrders/logic/WorkRequirementScheduleLogic";
import {
    saveWorkRequirementSchedule,
    getWorkRequirement,
  } from "screens/WorkOrders/logic/WorkRequirementScheduleLogic";
import { copyBudgetedResourcesToJobs } from "../logic/CopyJobsLogic";
import { useNavigate } from "react-router-dom";
import { GetService } from "screens/Services/logic/GetService";
  
const JobSchedule = ({ open, handleClose, services = [], fetchWorkRequirements, requestID, toast, onBack }) => {

  const MySwal = withReactContent(Swal);
  const theme = useTheme();

  // === Job Type & Recurrence ===
  const [jobType, setJobType] = useState("one-time");
  const [recurringType, setRecurringType] = useState("weekly");
  const [monthlyOptions, setMonthlyOptions] = useState("weekOfMonth");

  // === One-Time ===
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);

  // Separate date and time for one-time jobs to prevent auto time selection
  const [startDateOnly, setStartDateOnly] = useState(null);
  const [endDateOnly, setEndDateOnly] = useState(null);
  const [startTimeOnly, setStartTimeOnly] = useState(null);
  const [endTimeOnly, setEndTimeOnly] = useState(null);

  // === Common Recurring ===
  const [firstOccDate, setFirstOccDate] = useState(null);
  const [occurrences, setOccurrences] = useState("");
  const [excludeWeekends, setExcludeWeekends] = useState(false);
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [lastOccDate, setLastOccDate] = useState(null);
  const [calculatedLastOccDate, setCalculatedLastOccDate] = useState(null); // The actual LOD based on pattern

  // === Weekly / Bi-Weekly ===
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState(['mon']);

  // === Monthly week-of-month ===
  const [selectedWeeksOfMonth, setSelectedWeeksOfMonth] = useState(["first"]);

  // === Interval ===
  const [intervalDays, setIntervalDays] = useState("");

  // === Times for recurring ===
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [errors, setErrors] = useState({
    startDateTime: '',
    endDateTime: '',
    occurrences: '',
    selectedDaysOfWeek: '',
    firstOccDate: '',
    intervalDays: '',
    selectedWeeksOfMonth: ''
  });

  // Flag to track if form has been initialized to prevent re-initialization
  const [isInitialized, setIsInitialized] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  
  // Loading state for Schedule button
  const [isScheduling, setIsScheduling] = useState(false);

  // Ref to track if occurrences change is from lastOccDate edit (to prevent circular updates)
  const isUpdatingFromLastOccDate = React.useRef(false);

  const navigate = useNavigate();

  // helper map for day toggles
  const dayCodeToNum = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thurs: 4,
    fri: 5,
    sat: 6,
  };

  const numToDayCode = {
    0: "sun",
    1: "mon",
    2: "tue",
    3: "wed",
    4: "thurs",
    5: "fri",
    6: "sat",
  };

  const weekLabelToKey = {
    First:  "first",
    Second: "second",
    Third:  "third",
    Fourth: "fourth",
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        setErrors({
          startDateTime: '',
          endDateTime: '',
          occurrences: '',
          selectedDaysOfWeek: '',
          firstOccDate: '',
          intervalDays: '',
          selectedWeeksOfMonth: ''
        });
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [errors]);

  useEffect(() => {
    if (firstOccDate && occurrences) {
      const occNum = Number(occurrences);
      let computed;
      switch (recurringType) {
        case 'weekly':
          // For weekly, find the anchor date (selected day of week on or after firstOccDate)
          if (selectedDaysOfWeek.length > 0) {
            const dow = dayCodeToNum[selectedDaysOfWeek[0]];
            let firstDow = dayjs(firstOccDate).day(dow);
            if (firstDow.isBefore(dayjs(firstOccDate), "day")) {
              firstDow = firstDow.add(1, "week");
            }
            computed = firstDow.add((occNum - 1) * 1, "week");
          } else {
            computed = dayjs(firstOccDate).add(occNum - 1, 'week');
          }
          break;
        case 'bi-weekly':
          // For bi-weekly, same as weekly but with 2-week periods
          if (selectedDaysOfWeek.length > 0) {
            const dow = dayCodeToNum[selectedDaysOfWeek[0]];
            let firstDow = dayjs(firstOccDate).day(dow);
            if (firstDow.isBefore(dayjs(firstOccDate), "day")) {
              firstDow = firstDow.add(1, "week");
            }
            computed = firstDow.add((occNum - 1) * 2, "week");
          } else {
            computed = dayjs(firstOccDate).add((occNum - 1) * 2, 'week');
          }
          break;
        case 'Interval-Based':
          computed = dayjs(firstOccDate).add((occNum - 1) * intervalDays, 'day');
          break;
        case 'monthly':
          // For monthly, we need to iterate through months to find the actual last occurrence
          if (monthlyOptions === "weekOfMonth" && selectedWeeksOfMonth.length > 0 && selectedDaysOfWeek.length > 0) {
            const weekNumMap = { first: 1, second: 2, third: 3, fourth: 4 };
            const weekNums = selectedWeeksOfMonth.map(w => weekNumMap[w]).filter(n => n);
            const dow = dayCodeToNum[selectedDaysOfWeek[0]];
            
            let count = 0;
            let mOffset = 0;
            let lastDate = firstOccDate;
            
            while (count < occNum) {
              const monthStart = dayjs(firstOccDate).startOf("month").add(mOffset, "month");
              const year = monthStart.year();
              const month = monthStart.month();
              
              for (let wn of weekNums) {
                const first = dayjs().year(year).month(month).date(1);
                const offset = (dow - first.day() + 7) % 7;
                const dt = first.add(offset + (wn - 1) * 7, "day");
                
                if (!dt.isBefore(dayjs(firstOccDate), "day")) {
                  lastDate = dt;
                  count++;
                  if (count >= occNum) break;
                }
              }
              mOffset++;
            }
            computed = lastDate;
          } else {
            // Fallback to simple month addition for billingDay option
            computed = dayjs(firstOccDate).add(occNum - 1, 'month');
          }
          break;
        default:
          computed = firstOccDate;
      }
      
      // Always update the calculated last occurrence date (for LOD display)
      setCalculatedLastOccDate(computed);
      
      // Only update the actual lastOccDate field if this change is NOT from manual lastOccDate edit
      if (!isUpdatingFromLastOccDate.current) {
        setLastOccDate(computed);
      }
      
      // Reset the flag
      isUpdatingFromLastOccDate.current = false;
    }
  }, [firstOccDate, occurrences, recurringType, intervalDays, selectedDaysOfWeek, selectedWeeksOfMonth, monthlyOptions]);
  
  useEffect(() => {

    if (!open || services.length === 0 || isInitialized) return;
    console.log(services)
    const svc = services[0]; // if you ever support multi-service that you want to prefill all at once, you'd loop here
    if(services.length === 1) {
      if(svc && svc.jobType === "Recurring"){
        setJobType("recurring");
        if(svc.numberOfOccurrences > 0 && svc.numberOfOccurrences !== null){
          setOccurrences(svc.numberOfOccurrences)
        }
      } 
      // if(svc && !svc.jobType){
      //   setJobType("one-time");
      //   if(svc.numberOfOccurrences > 0 && svc.numberOfOccurrences !== null){
      //     setOccurrences(svc.numberOfOccurrences)
      //   }
      // }
    }    
    const anyJobs = (svc.jobs || []).filter(j => j.occurrenceNo > 0);
// console.log(anyJobs)
    if (anyJobs.length > 0) {
      setRescheduling(true);
    }
    const realJobs = (svc.jobs || []).filter(j => j.occurrenceNo > 0 && j.scheduleStartDate && j.scheduleEndDate);

    if (anyJobs.length === 0) {
      // nothing scheduled yet → leave defaults (probably "one-time")
      setIsInitialized(true);      
      return;
    }

    if (svc.jobType === "One Time" || svc.jobType === null) {
      setJobType("one-time");
      // grab the very first real job to get its start/end date-times
      const firstJob = anyJobs[0];
      console.log(firstJob)
      // Set separate date and time values
      if (firstJob.scheduleStartDate) {
        setStartDateOnly(dayjs(firstJob.scheduleStartDate));
      }
      if (firstJob.scheduleStartTime) {
        setStartTimeOnly(dayjs(`1970-01-01T${firstJob.scheduleStartTime}`));
      }
      if (firstJob.scheduleEndDate) {
        setEndDateOnly(dayjs(firstJob.scheduleEndDate));
      }
      if (firstJob.scheduleEndTime) {
        setEndTimeOnly(dayjs(`1970-01-01T${firstJob.scheduleEndTime}`));
      }
      
      // Also set combined datetime for backward compatibility if both exist
      if (firstJob.scheduleStartDate && firstJob.scheduleStartTime) {
        const startDate = dayjs(firstJob.scheduleStartDate);
        const startTime = dayjs(`1970-01-01T${firstJob.scheduleStartTime}`);
        const first = startDate.hour(startTime.hour()).minute(startTime.minute());
      setStartDateTime(first);
      }
      
      if (firstJob.scheduleEndDate && firstJob.scheduleEndTime) {
        const endDate = dayjs(firstJob.scheduleEndDate);
        const endTime = dayjs(`1970-01-01T${firstJob.scheduleEndTime}`);
        const last = endDate.hour(endTime.hour()).minute(endTime.minute());
      setEndDateTime(last);
      }
      
      setIsInitialized(true);
      return; // done, skip the recurring logic
    }
  
    // we have at least one real occurrence → switch to recurring
    setJobType("recurring");
  
    let freqKey;
    switch (svc.frequency) {
      case "Weekly":
        freqKey = "weekly";
        break;
      case "Bi-Weekly":
        freqKey = "bi-weekly";
        break;
      case "Monthly":
        freqKey = "monthly";
        break;
      case "Interval-Based":
        freqKey = "Interval-Based";
        break;
      default:
        // fallback to a lowercased version for anything else
        freqKey = svc?.frequency?.toLowerCase();
    }
    setRecurringType(freqKey);
  
    // common fields:
    setOccurrences(String(svc.numberOfOccurrences));
    setExcludeWeekends(!!svc.excludeWeekends);
    setExcludeHolidays(!!svc.excludeHolidays);
    setFirstOccDate(dayjs(svc.firstOccurrenceDate, "YYYY-MM-DD"));
  
    // daysOfWeek:
    let dowArr = svc.daysOfWeek;
    if (typeof dowArr === "string") {
      try { dowArr = JSON.parse(dowArr); } catch {}
    }
    if (Array.isArray(dowArr) && dowArr.length > 0) {
      setSelectedDaysOfWeek(dowArr.map(n => numToDayCode[n]));
    }
  
    // monthlyOptions & weeksOfMonth if needed
    if (svc.frequency === "Monthly") {
      // if they used week-of-month…
      if (svc.weekConfiguration) {
        setMonthlyOptions("weekOfMonth");
        setSelectedWeeksOfMonth([ weekLabelToKey[ svc.weekConfiguration ] ]);
      } else {
        setMonthlyOptions("weekOfMonth");
        // leave selectedWeeksOfMonth as ['first']
      }
    }
  
    // intervalDays:
    if (svc.frequency === "Interval-Based") {
      setIntervalDays(String(svc.intervalDays || ""));
    }
  
    // pick the **time** portion from the very first real job:
    const firstJob = realJobs[0];
    if (firstJob.scheduleStartTime) {
      const s = dayjs(`1970-01-01T${firstJob.scheduleStartTime}`);
    setStartTime(s);
    }
    if (firstJob.scheduleEndTime) {
      const e = dayjs(`1970-01-01T${firstJob.scheduleEndTime}`);
    setEndTime(e);
    }
  
    // Mark as initialized to prevent re-initialization
    setIsInitialized(true);
  }, [open, services, isInitialized]);

  // const [serviceFlatRate, setServiceFlatRate] = useState(0);

  const getService = async (serviceOfferingID) => {
    const pageSize = 10;
    const pageNumber = 1;
    const keyword = "";
    let flatRate = 0;
  
    try {
      const response = await GetService(serviceOfferingID, pageSize, pageNumber, keyword, navigate);
      if (!response.isError && response.result && response.result.length > 0) {
        flatRate = response.result[0]?.flatRate || 0;
        return flatRate;
      } else {
        console.warn(`No service found for serviceOfferingID: ${serviceOfferingID}`);
        return 0;
      }
    } catch (error) {
      console.error(`Error fetching service for serviceOfferingID ${serviceOfferingID}:`, error);
      return 0;
    }
  }

// --- Build the API payload for one service ---
const buildPayload = async (svc) => {
  // determine how many occurrences
  const numOcc = jobType === "one-time" ? 1 : Number(occurrences);
  // if (jobType !== "one-time" && (!occurrences || numOcc < 1)) {
  //   throw new Error("Occurrences must be at least 1");
  // }
  const serviceFlatRate = await getService(svc.subServiceID);
  
  // Ensure serviceFlatRate is a valid number
  const validFlatRate = typeof serviceFlatRate === 'number' && !isNaN(serviceFlatRate) ? serviceFlatRate : 0;

  // base fields
  const base = {
    workReqID: svc.workReqID,
    entityID: svc.entityID,
    subServiceID: svc.subServiceID,
    bundleID: svc.bundleID,
    jobType: jobType === "one-time" ? "One Time" : "Recurring",
    billingType: svc.billingType, //"Fixed"
    numberOfOccurrences: numOcc,
    firstOccurrenceDate:
      jobType === "one-time" && !startDateTime
        ? null
        : (jobType === "one-time" ? startDateTime : firstOccDate)
        ? dayjs(jobType === "one-time" ? startDateTime : firstOccDate).format("YYYY-MM-DDTHH:mm:ss")
        : null,
    excludeWeekends,
    excludeHolidays,
    jobs: [],
  };

  // ONE-TIME
  if (jobType === "one-time") {
    // Determine job status - ALL 4 fields must be present for Scheduled status
    const hasCompleteSchedule = startDateOnly && endDateOnly && startTimeOnly && endTimeOnly;
    const jobStatus = hasCompleteSchedule ? "Scheduled" : "Unscheduled";
    
    base.jobs.push({
      jobID: 0,
      flatRate: validFlatRate,
      billingType: "Fixed",
      occurrenceNo: 1,
      scheduleStartDate: startDateOnly
        ? dayjs(startDateOnly).format("YYYY-MM-DD")
        : null,
      scheduleEndDate: endDateOnly
        ? dayjs(endDateOnly).format("YYYY-MM-DD")
        : null,
      scheduleStartTime: startTimeOnly
        ? dayjs(startTimeOnly).format("HH:mm:ss")
        : null,
      scheduleEndTime: endTimeOnly
        ? dayjs(endTimeOnly).format("HH:mm:ss")
        : null,
      status: jobStatus,
    });
    return base;
  }

  // recurring-specific fields
  base.frequency = recurringType;
  if (recurringType === "Interval-Based") {
    base.intervalDays = Number(intervalDays);
  }

  // month-of-week configuration
  if (recurringType === "monthly" && monthlyOptions === "weekOfMonth") {
    const weekNumMap   = { first: 1, second: 2, third: 3, fourth: 4 };
    const weekLabelMap = { first: "First", second: "Second", third: "Third", fourth: "Fourth" };
    const weekNums = selectedWeeksOfMonth
      .map(w => weekNumMap[w])
      .filter(n => n !== undefined);
    base.weekConfiguration  = weekLabelMap[selectedWeeksOfMonth[0]] || weekLabelMap.first;
    base.monthConfiguration = weekNums;
    base.daysOfWeek         = selectedDaysOfWeek.map(d => dayCodeToNum[d]);
  }

  // build the jobs array
  let jobs = [];

  // WEEKLY / BI-WEEKLY
  if (["weekly", "bi-weekly"].includes(recurringType)) {
    const period = recurringType === "weekly" ? 1 : 2;
    const days   = selectedDaysOfWeek.map(d => dayCodeToNum[d])
                      .filter(n => n !== undefined)
                      .sort();
    base.daysOfWeek = days;

    const anchors = days.map(dow => {
      let firstDow = dayjs(firstOccDate).day(dow);
      if (firstDow.isBefore(dayjs(firstOccDate), "day")) {
        const bump = recurringType === "bi-weekly" ? 1 : period;
        firstDow = firstDow.add(bump, "week");
      }
      return firstDow;
    });

    for (let cycle = 0; cycle < numOcc; cycle++) {
      for (let anchor of anchors) {
        const dt = anchor.add(cycle * period, "week");
        
        // Apply time if available, otherwise use date only
        const start = startTime 
          ? dt.hour(dayjs(startTime).hour()).minute(dayjs(startTime).minute())
          : dt;
        const end = endTime 
          ? dt.hour(dayjs(endTime).hour()).minute(dayjs(endTime).minute())
          : dt;
          
        // Check if all date/time fields are present for complete schedule
        const startDateStr = start.format("YYYY-MM-DD");
        const endDateStr = end.format("YYYY-MM-DD");
        const startTimeStr = startTime ? start.format("HH:mm:ss") : null;
        const endTimeStr = endTime ? end.format("HH:mm:ss") : null;
        
        const hasCompleteSchedule = startDateStr && endDateStr && startTimeStr && endTimeStr;
        const jobStatus = hasCompleteSchedule ? "Scheduled" : "Unscheduled";
        
        jobs.push({
          jobID: 0,
          flatRate: validFlatRate,
          billingType: "Fixed",
          scheduleStartDate: startDateStr,
          scheduleEndDate: endDateStr,
          scheduleStartTime: startTimeStr,
          scheduleEndTime: endTimeStr,
          status: jobStatus,
        });
      }
    }
  }
  // INTERVAL
  else if (recurringType === "Interval-Based") {
    for (let i = 0; i < numOcc; i++) {
      const dt = dayjs(firstOccDate).add(i * Number(intervalDays), "day");
      
      // Apply time if available, otherwise use date only
      const start = startTime 
        ? dt.hour(dayjs(startTime).hour()).minute(dayjs(startTime).minute())
        : dt;
      const end = endTime 
        ? dt.hour(dayjs(endTime).hour()).minute(dayjs(endTime).minute())
        : dt;
        
      // Check if all date/time fields are present for complete schedule
      const startDateStr = start.format("YYYY-MM-DD");
      const endDateStr = end.format("YYYY-MM-DD");
      const startTimeStr = startTime ? start.format("HH:mm:ss") : null;
      const endTimeStr = endTime ? end.format("HH:mm:ss") : null;
      
      const hasCompleteSchedule = startDateStr && endDateStr && startTimeStr && endTimeStr;
      const jobStatus = hasCompleteSchedule ? "Scheduled" : "Unscheduled";
      
      jobs.push({
        jobID: 0,
        flatRate: validFlatRate,
        billingType: "Fixed",
        scheduleStartDate: startDateStr,
        scheduleEndDate: endDateStr,
        scheduleStartTime: startTimeStr,
        scheduleEndTime: endTimeStr,
        status: jobStatus,
      });
    }
  }
  // MONTHLY
  else if (recurringType === "monthly") {
    // billing day of month
    if (monthlyOptions === "billingDay") {
      let count = 0,
          mOffset = 0;
      while (count < numOcc) {
        const candidate = dayjs(firstOccDate).add(mOffset, "month");
        if (!candidate.isBefore(dayjs(firstOccDate), "day")) {
          // Apply time if available, otherwise use date only
          const start = startTime 
            ? candidate.hour(dayjs(startTime).hour()).minute(dayjs(startTime).minute())
            : candidate;
          const end = endTime 
            ? candidate.hour(dayjs(endTime).hour()).minute(dayjs(endTime).minute())
            : candidate;
            
          // Check if all date/time fields are present for complete schedule
          const startDateStr = start.format("YYYY-MM-DD");
          const endDateStr = end.format("YYYY-MM-DD");
          const startTimeStr = startTime ? start.format("HH:mm:ss") : null;
          const endTimeStr = endTime ? end.format("HH:mm:ss") : null;
          
          const hasCompleteSchedule = startDateStr && endDateStr && startTimeStr && endTimeStr;
          const jobStatus = hasCompleteSchedule ? "Scheduled" : "Unscheduled";
          
          jobs.push({
            jobID: 0, flatRate: validFlatRate, billingType: "Fixed",
            scheduleStartDate: startDateStr,
            scheduleEndDate: endDateStr,
            scheduleStartTime: startTimeStr,
            scheduleEndTime: endTimeStr,
            status: jobStatus,
          });
          count++;
        }
        mOffset++;
      }
    }
    // week-of-month
    else {
      const weekNumMap = { first: 1, second: 2, third: 3, fourth: 4 };
      const weekNums   = selectedWeeksOfMonth.map(w => weekNumMap[w]).filter(n => n);
      let count = 0,
          mOffset = 0;

      outer: while (count < numOcc) {
        const monthStart = dayjs(firstOccDate).startOf("month").add(mOffset, "month");
        const year       = monthStart.year(),
              month      = monthStart.month();

        for (let wn of weekNums) {
          for (let d of selectedDaysOfWeek) {
            const dow    = dayCodeToNum[d],
                  first  = dayjs().year(year).month(month).date(1),
                  offset = (dow - first.day() + 7) % 7,
                  dt     = first.add(offset + (wn - 1) * 7, "day");

            if (!dt.isBefore(dayjs(firstOccDate), "day")) {
              // Apply time if available, otherwise use date only
              const start = startTime 
                ? dt.hour(dayjs(startTime).hour()).minute(dayjs(startTime).minute())
                : dt;
              const end = endTime 
                ? dt.hour(dayjs(endTime).hour()).minute(dayjs(endTime).minute())
                : dt;
                
              // Check if all date/time fields are present for complete schedule
              const startDateStr = start.format("YYYY-MM-DD");
              const endDateStr = end.format("YYYY-MM-DD");
              const startTimeStr = startTime ? start.format("HH:mm:ss") : null;
              const endTimeStr = endTime ? end.format("HH:mm:ss") : null;
              
              const hasCompleteSchedule = startDateStr && endDateStr && startTimeStr && endTimeStr;
              const jobStatus = hasCompleteSchedule ? "Scheduled" : "Unscheduled";
              
              jobs.push({
                jobID: 0, flatRate: validFlatRate, billingType: "Fixed",
                scheduleStartDate: startDateStr,
                scheduleEndDate: endDateStr,
                scheduleStartTime: startTimeStr,
                scheduleEndTime: endTimeStr,
                status: jobStatus,
              });
              count++;
              if (count >= numOcc) break outer;
            }
          }
        }
        mOffset++;
      }
    }
  }

  // sort & assign occurrenceNo
  jobs
    .sort((a, b) => {
      // Handle cases where date or time might be null
      const aDate = a.scheduleStartDate || "1970-01-01";
      const aTime = a.scheduleStartTime || "00:00:00";
      const bDate = b.scheduleStartDate || "1970-01-01";
      const bTime = b.scheduleStartTime || "00:00:00";
      
      const aDateTime = dayjs(`${aDate}T${aTime}`);
      const bDateTime = dayjs(`${bDate}T${bTime}`);
      return aDateTime.isBefore(bDateTime) ? -1 : 1;
    })
    .forEach((job, idx) => { job.occurrenceNo = idx + 1; });

  base.jobs = jobs;
  return base;
};

  const handleReset = () => {
    setJobType("one-time");
    setRecurringType("weekly");
    setMonthlyOptions("weekOfMonth");
    setStartDateTime(null);
    setEndDateTime(null);
    setStartDateOnly(null);
    setEndDateOnly(null);
    setStartTimeOnly(null);
    setEndTimeOnly(null);
    setFirstOccDate(null);
    setOccurrences("");
    setExcludeWeekends(false);
    setExcludeHolidays(false);
    setSelectedDaysOfWeek(["mon"]);
    setSelectedWeeksOfMonth([]);
    setIntervalDays("");
    setStartTime(null);
    setEndTime(null);
    setLastOccDate(null);
    setCalculatedLastOccDate(null);
    setErrors({ startDateTime: '', endDateTime: '', occurrences: '', selectedDaysOfWeek: '', firstOccDate: '', intervalDays: '', selectedWeeksOfMonth: '' });
    setIsInitialized(false); // Reset initialization flag
  };

  const resetOnRecurringTypeChange = () => {
    setMonthlyOptions("weekOfMonth");
    setStartDateTime(null);
    setEndDateTime(null);
    setFirstOccDate(null);
    setOccurrences("");
    setExcludeWeekends(false);
    setExcludeHolidays(false);
    setSelectedDaysOfWeek(["mon"]);
    setSelectedWeeksOfMonth([]);
    setIntervalDays("");
    setStartTime(null);
    setEndTime(null);
    setLastOccDate(null);
    setCalculatedLastOccDate(null);
    setErrors({ startDateTime: '', endDateTime: '', occurrences: '', selectedDaysOfWeek: '', firstOccDate: '', intervalDays: '', selectedWeeksOfMonth: '' });
    // Don't reset isInitialized here as user is just changing recurring type
  };

  // 2) Wrap your on‐close to reset then bubble up
  const onDialogClose = () => {
    handleReset();
    handleClose();
  };

// when user picks a last-occurrence date, recalc occurrences based on actual valid dates
const handleLastOccDateChange = (date) => {
  if (!date) {
    setLastOccDate(null);
    setCalculatedLastOccDate(null);
    return;
  }
  setLastOccDate(date);
  
  if (!firstOccDate) {
    return;
  }
  
  let newOcc = 1;
  
  switch (recurringType) {
    case 'weekly':
      if (selectedDaysOfWeek.length > 0) {
        const dow = dayCodeToNum[selectedDaysOfWeek[0]];
        let current = dayjs(firstOccDate).day(dow);
        if (current.isBefore(dayjs(firstOccDate), "day")) {
          current = current.add(1, "week");
        }
        
        let count = 0;
        const targetDate = dayjs(date);
        while (!current.isAfter(targetDate, "day")) {
          count++;
          current = current.add(1, "week");
        }
        newOcc = Math.max(1, count);
      }
      break;
      
    case 'bi-weekly':
      if (selectedDaysOfWeek.length > 0) {
        const dow = dayCodeToNum[selectedDaysOfWeek[0]];
        let current = dayjs(firstOccDate).day(dow);
        if (current.isBefore(dayjs(firstOccDate), "day")) {
          current = current.add(1, "week");
        }
        
        let count = 0;
        const targetDate = dayjs(date);
        while (!current.isAfter(targetDate, "day")) {
          count++;
          current = current.add(2, "week");
        }
        newOcc = Math.max(1, count);
      }
      break;
      
    case 'Interval-Based':
      if (intervalDays > 0) {
        let current = dayjs(firstOccDate);
        let count = 0;
        const targetDate = dayjs(date);
        while (!current.isAfter(targetDate, "day")) {
          count++;
          current = current.add(Number(intervalDays), "day");
        }
        newOcc = Math.max(1, count);
      }
      break;
      
    case 'monthly':
      if (monthlyOptions === "weekOfMonth" && selectedWeeksOfMonth.length > 0 && selectedDaysOfWeek.length > 0) {
        const weekNumMap = { first: 1, second: 2, third: 3, fourth: 4 };
        const weekNums = selectedWeeksOfMonth.map(w => weekNumMap[w]).filter(n => n);
        const dow = dayCodeToNum[selectedDaysOfWeek[0]];
        
        let count = 0;
        let mOffset = 0;
        const targetDate = dayjs(date);
        
        while (true) {
          const monthStart = dayjs(firstOccDate).startOf("month").add(mOffset, "month");
          const year = monthStart.year();
          const month = monthStart.month();
          
          let foundInMonth = false;
          for (let wn of weekNums) {
            const first = dayjs().year(year).month(month).date(1);
            const offset = (dow - first.day() + 7) % 7;
            const dt = first.add(offset + (wn - 1) * 7, "day");
            
            if (!dt.isBefore(dayjs(firstOccDate), "day") && !dt.isAfter(targetDate, "day")) {
              count++;
              foundInMonth = true;
            }
          }
          
          // Stop if we've gone past the selected date
          if (monthStart.isAfter(targetDate, "month")) {
            break;
          }
          
          mOffset++;
          
          // Safety check to prevent infinite loop
          if (mOffset > 1200) break; // 100 years
        }
        newOcc = Math.max(1, count);
      } else {
        // Fallback for billingDay option
        const diff = dayjs(date).diff(firstOccDate, 'month');
        newOcc = Math.max(1, diff + 1);
      }
      break;
      
    default:
      newOcc = 1;
  }
  
  // Set the flag to prevent useEffect from overwriting lastOccDate
  isUpdatingFromLastOccDate.current = true;
  
  // Update occurrences, which will trigger useEffect to calculate calculatedLastOccDate
  setOccurrences(String(newOcc));
  if (errors.occurrences) setErrors(e => ({ ...e, occurrences: '' }));
};

  // Recalculate occurrences when firstOccDate changes and lastOccDate already exists
  useEffect(() => {
    if (firstOccDate && lastOccDate && !isUpdatingFromLastOccDate.current) {
      // Trigger recalculation by calling handleLastOccDateChange with existing lastOccDate
      handleLastOccDateChange(lastOccDate);
    }
  }, [firstOccDate]);

  // --- Save & close ---
  const handleSchedule = async () => {
    // Set loading state

    setIsScheduling(true);
    // reset errors
    setErrors({ startDateTime: '', endDateTime: '', occurrences: '', selectedDaysOfWeek: '', firstOccDate: '', intervalDays: '', selectedWeeksOfMonth: '' });
  
    let hasError = false;

  if (jobType === 'one-time') {

    // Check if we have both dates and they are in correct order
    if (startDateOnly && endDateOnly) {
      let startCompare = dayjs(startDateOnly);
      let endCompare = dayjs(endDateOnly);
      
      // If times are also set, include them in comparison
      if (startTimeOnly) {
        startCompare = startCompare.hour(startTimeOnly.hour()).minute(startTimeOnly.minute());
      }
      if (endTimeOnly) {
        endCompare = endCompare.hour(endTimeOnly.hour()).minute(endTimeOnly.minute());
      }
      
      if (startCompare.isAfter(endCompare)) {
        setErrors(e => ({ ...e, endDateTime: 'End date time must be after start date time' }));
      hasError = true;
      }
    }

  }

  if(jobType==='recurring' && monthlyOptions === "billingDay"){
    if (!firstOccDate) {
      // similar pattern for firstOccDate…
      setErrors(e => ({ ...e, firstOccDate: 'Please select a day' }));
      hasError = true;
    }
  }

    if(jobType==='recurring' && monthlyOptions !== "billingDay"){
    if (!firstOccDate) {
      // similar pattern for firstOccDate…
      setErrors(e => ({ ...e, firstOccDate: 'Please enter first occurrence date' }));
      hasError = true;
    }
  }
  
    // 3) if recurring, occurrences + days/time required

    if (jobType === 'recurring' && recurringType === 'monthly' && monthlyOptions === 'weekOfMonth') {
    if (selectedDaysOfWeek.length === 0) {

      setErrors(e => ({ ...e, selectedDaysOfWeek: 'Please select at least one day of week' }));
      hasError = true;
    }

    if (selectedWeeksOfMonth.length === 0) {

      setErrors(e => ({ ...e, selectedWeeksOfMonth: 'Please select at least one week of month' }));
      hasError = true;
    }
  }

  if(jobType==='recurring' && recurringType==='interval-based'){
    if(!intervalDays) {
      setErrors(e => ({ ...e, intervalDays: 'Please select interval days' }));
      hasError = true;
    }
  }


    if (jobType === 'recurring') {
      if (!occurrences || Number(occurrences) < 1) {
        setErrors(e => ({ ...e, occurrences: 'Please enter at least 1 occurrence' }));
        hasError = true;
      }
      
    }
  
    if (hasError) {
      setIsScheduling(false);
      return;
    }
  
    let result;
    // console.log("rescheduling = " +rescheduling)

    if(rescheduling) {
      result = await MySwal.fire({
        title: '<strong>Are you sure?</strong>',
        html: `<span>Making this change will remove any existing resources assigned to this job.
              If you want to keep the resources, please update the job from the Job Details page.</span>`,
        showCancelButton: true,
        confirmButtonText: 'Continue!',
        cancelButtonText: 'Discard changes',
        width: '390px',
        padding: '1rem',
        focusConfirm: false,
        showCloseButton: true,
        closeButtonHtml: '&times;',
        customClass: {
          popup: theme.palette.mode === 'dark' ? 'custom-swal-popup-dark' : 'custom-swal-popup',
          title: theme.palette.mode === 'dark' ? 'custom-swal-title-dark' : 'custom-swal-title',
          htmlContainer: 'custom-swal-html',
          confirmButton: 'custom-swal-confirm',
          cancelButton: theme.palette.mode === 'dark' ? 'custom-swal-cancel-dark' : 'custom-swal-cancel',
          closeButton: 'custom-swal-close'
        }
      });
    } else {
       result = {
        isConfirmed: true
      }
    }


    if (result.isConfirmed) {

    try {
      for (let svc of services) {
        // 1) fetch existing WR
        const wr = await getWorkRequirement({
          workReqID: svc.workReqID,
          entityID: svc.entityID
        });
        // console.log(wr);
        
        // 2) find the zero-occurrence job
        let zeroJob = wr?.jobs?.find(j => j.occurrenceNo === 0);
 
        // 3) if found, reset via saveWorkRequirementSchedule with only that job
        if (zeroJob) {
          // console.log(zeroJob);
         const resetPayload = {
          workReqID: wr.workReqID,
          entityID: wr.entityID,
          subServiceID: wr.subServiceID,
          bundleID: wr.bundleID,
          jobType: wr.jobType,            // "Recurring" or "One Time"
          billingType: wr.billingType,    // e.g. "Fixed"
          frequency: wr.frequency,        // e.g. "Weekly"
          numberOfOccurrences: wr.numberOfOccurrences,
          firstOccurrenceDate: wr.firstOccurrenceDate,
          excludeWeekends: wr.excludeWeekends,
          excludeHolidays: wr.excludeHolidays,
          weekConfiguration: wr.weekConfiguration,
          monthConfiguration: Array.isArray(wr.monthConfiguration)
            ? wr.monthConfiguration : JSON.parse(wr.monthConfiguration || '[]'),
          intervalDays: wr.intervalDays,
          daysOfWeek: Array.isArray(wr.daysOfWeek)
            ? wr.daysOfWeek : JSON.parse(wr.daysOfWeek || '[]'),
          jobs: [ zeroJob ]
         };
         
         await saveWorkRequirementSchedule(resetPayload, navigate);
        }

        if (!zeroJob && wr?.jobs?.length > 0) {
          const resetPayload = {
            workReqID: wr.workReqID,
            entityID: wr.entityID,
            subServiceID: wr.subServiceID,
            bundleID: wr.bundleID,
            jobType: wr.jobType,            // "Recurring" or "One Time"
            billingType: wr.billingType,    // e.g. "Fixed"
            frequency: wr.frequency,        // e.g. "Weekly"
            numberOfOccurrences: wr.numberOfOccurrences,
            firstOccurrenceDate: wr.firstOccurrenceDate,
            excludeWeekends: wr.excludeWeekends,
            excludeHolidays: wr.excludeHolidays,
            weekConfiguration: wr.weekConfiguration,
            monthConfiguration: Array.isArray(wr.monthConfiguration)
            ? wr.monthConfiguration : JSON.parse(wr.monthConfiguration || '[]'),
            intervalDays: wr.intervalDays,
            daysOfWeek: Array.isArray(wr.daysOfWeek)
              ? wr.daysOfWeek : JSON.parse(wr.daysOfWeek || '[]'),
            jobs: []
          };
  
          await saveWorkRequirementSchedule(resetPayload);
         }
 
        // 4) now build your brand-new schedule payload (all new jobs)
        const payload = await buildPayload(svc);
  
        // 5) prepend the old zeroJob so it survives
        if (zeroJob) {
          payload.jobs.unshift({ ...zeroJob, occurrenceNo: 0 });
        }
    
        // 6) save the full schedule
        // console.log(payload)
        const schedules = await saveWorkRequirementSchedule(payload);
        if (schedules && !schedules.isError && schedules?.result?.workReqID > 0) {
          // 1. Fetch the full work requirement with all jobs using workReqID
          const wrWithJobs = await getWorkRequirement({
            workReqID: schedules.result.workReqID,
            entityID: 0
            // If needed, add entityID or other params here
          });

          // 2. Now pass the jobs array to your copy function
          if (wrWithJobs && wrWithJobs.jobs && wrWithJobs.jobs.length > 0) {
            
            await copyBudgetedResourcesToJobs(
              schedules.result.workReqID,
              wrWithJobs.jobs,
              navigate
            );
          }
        }

      }
      toast.success("Schedule saved successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
      // refresh & close
      fetchWorkRequirements(requestID);
      
      handleReset();
      handleClose();
      setIsScheduling(false);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to save schedule", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
    } finally {
      setIsScheduling(false);
    }


    } else {

      handleReset();
      handleClose();
      setIsScheduling(false);
      setRescheduling(false);

    }
 
  };

    // 1. Handler for when the user picks a new date:
  const handleFirstOccDateChange = (date) => {
    if (!date) {
      setFirstOccDate(null);
      return;
    }
    const d = dayjs(date);
    setFirstOccDate(d);
    // auto‐sync the Day‐of‐Week toggle:
    const dowCode = numToDayCode[d.day()];         // e.g. 0→"sun", 1→"mon", …
    setSelectedDaysOfWeek([dowCode]);
  };

  // 2. Handler for when the user toggles a new day:
  const handleDayOfWeekChange = (_, newDays) => {
    if (newDays.length === 0) return;
    const selected = newDays[newDays.length - 1];      // e.g. "wed"
    setSelectedDaysOfWeek([selected]);
  
    // compute today at midnight
    const today = dayjs().startOf("day");
    // find numeric 0–6 for that toggle
    const targetDow = dayCodeToNum[selected];
    // how many days to add to get to the next targetDow (0..6)
    const daysToAdd = (targetDow - today.day() + 7) % 7;
    // if daysToAdd is 0, it picks today; otherwise 1–6 days ahead
    const nextDate = today.add(daysToAdd, "day");
    setFirstOccDate(nextDate);
  };

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={onDialogClose}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #D9D9D9",
            paddingBottom: "20px !important",
          }}
        >
          <Box>
            <Typography variant="h4" component="h2">
              Schedule Jobs
            </Typography>
          </Box>
          <Tooltip title="Close">
            <IconButton onClick={onDialogClose}>
              <Icon icon="tabler:x" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent>
          {services.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle1">Scheduling for:</Typography>
              {services.map((s) => (
                <CustomChip
                  key={s.workReqID}
                  label={`${s.industryName} – ${s.subServiceName}`}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}

          <Grid container spacing={5}>
            {/* Job Type */}
            <Grid item xs={12}>
              <FormLabel
                required
                sx={{
                  color: theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.78) !important' : "rgba(47, 43, 61, 0.78)!important",
                  "& .MuiFormLabel-asterisk": { color: "error.main" },
                  marginBottom: "4px",
                  fontSize: "13px",
                }}
              >
                Job Type
              </FormLabel>
              <RadioGroup
                row
                name="jobType"
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
              >
                <FormControlLabel
                  value="one-time"
                  control={<Radio color="success" />}
                  label="One-Time"
                />
                <FormControlLabel
                  value="recurring"
                  control={<Radio color="success" />}
                  label="Recurring"
                />
              </RadioGroup>
            </Grid>

            {/* One-Time UI */}
            {jobType === "one-time" && (
              <>
              <Grid container spacing={5} sx={{ ml: 1 }}>
  {/* Start Date & Time */}
  <Grid item xs={12} md={6.1}>
    <FormLabel
      sx={{
        fontWeight: 400,
        fontSize: "13px",
        mb: "4px",
        color: "text.primary",
      }}
    >
      Start Date & Time
    </FormLabel>
    
    <Grid container spacing={2}>
      <Grid item xs={6} md={7}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
           
            value={startDateOnly}
            disablePast
            onChange={(newDate) => {
              setStartDateOnly(newDate);
              
              // Update combined datetime if time also exists
              if (newDate && startTimeOnly) {
                const combined = newDate
                  .hour(startTimeOnly.hour())
                  .minute(startTimeOnly.minute());
                setStartDateTime(combined);
              } else if (newDate) {
                // Only date, no time - set datetime to null to indicate date-only
                setStartDateTime(null);
              } else {
                setStartDateTime(null);
              }
              setErrors((e) => ({ ...e, startDateTime: "" }));
            }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: !!errors.startDateTime,
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
      
      <Grid item xs={5}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MobileTimePicker
           
            value={startTimeOnly}
            onChange={(newTime) => {
              setStartTimeOnly(newTime);
              
              // Update combined datetime if date also exists
              if (newTime && startDateOnly) {
                const combined = startDateOnly
                  .hour(newTime.hour())
                  .minute(newTime.minute());
                setStartDateTime(combined);
              } else if (newTime) {
                // Time without date - use today's date
                const today = dayjs().startOf('day');
                const withTime = today.hour(newTime.hour()).minute(newTime.minute());
                setStartDateTime(withTime);
                setStartDateOnly(today);
              } else {
                // Time cleared - keep date-only if date exists
                setStartDateTime(null);
              }
              setErrors((e) => ({ ...e, startDateTime: "" }));
            }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: !!errors.startDateTime,
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
    </Grid>
    {errors.startDateTime && (
      <FormHelperText error sx={{ mt: 0.5 }}>
        {errors.startDateTime}
      </FormHelperText>
    )}
  </Grid>

  {/* End Date & Time */}
  <Grid item xs={12} md={6.1}>
    <FormLabel
      sx={{
        fontWeight: 400,
        fontSize: "13px",
        mb: "4px",
        color: "text.primary",
      }}
    >
      End Date & Time
    </FormLabel>
    
    <Grid container spacing={2}>
      <Grid item xs={7}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            
            value={endDateOnly}
            disablePast
            onChange={(newDate) => {
              setEndDateOnly(newDate);
              
              // Update combined datetime if time also exists
              if (newDate && endTimeOnly) {
                const combined = newDate
                  .hour(endTimeOnly.hour())
                  .minute(endTimeOnly.minute());
                setEndDateTime(combined);
              } else if (newDate) {
                // Only date, no time - set datetime to null to indicate date-only
                setEndDateTime(null);
              } else {
                setEndDateTime(null);
              }
              setErrors((e) => ({ ...e, endDateTime: "" }));
            }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: !!errors.endDateTime,
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
      
      <Grid item xs={5}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MobileTimePicker
          
            value={endTimeOnly}
            onChange={(newTime) => {
              setEndTimeOnly(newTime);
              
              // Update combined datetime if date also exists
              if (newTime && endDateOnly) {
                const combined = endDateOnly
                  .hour(newTime.hour())
                  .minute(newTime.minute());
                setEndDateTime(combined);
              } else if (newTime) {
                // Time without date - use today's date
                const today = dayjs().startOf('day');
                const withTime = today.hour(newTime.hour()).minute(newTime.minute());
                setEndDateTime(withTime);
                setEndDateOnly(today);
              } else {
                // Time cleared - keep date-only if date exists
                setEndDateTime(null);
              }
              setErrors((e) => ({ ...e, endDateTime: "" }));
            }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: !!errors.endDateTime,
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
    </Grid>
    {errors.endDateTime && (
      <FormHelperText error sx={{ mt: 0.5 }}>
        {errors.endDateTime}
      </FormHelperText>
    )}
  </Grid>
</Grid>

              </>
            )}

            {/* Recurring UI */}
            {jobType === "recurring" && (
              <>
                {/* Recurring Type */}
                <Grid item xs={12} md={6.1}>
                  <CustomTextField
                    fullWidth
                    required
                    select
                    label="Recurring Type"
                    value={recurringType}
                    onChange={(e) => {
                      resetOnRecurringTypeChange();
                      setRecurringType(e.target.value)
                    }
                    }
                  >
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="bi-weekly">Bi-Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="Interval-Based">Interval Based</MenuItem>
                  </CustomTextField>
                </Grid>

                {/* Weekly / Bi-Weekly */}
                {(recurringType === "weekly" ||
                  recurringType === "bi-weekly") && (
                  <>
                    <Grid item xs={12} md={6.1}>
                      <FormLabel
                        required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                          "& .MuiFormLabel-asterisk": {
                            color: "error.main",
                          },
                        }}
                      >
                        First Occurrence Date
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={firstOccDate}
                          disablePast
                          minDate={dayjs()}
                          onChange={handleFirstOccDateChange}
                          sx={{ width: "100%" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </LocalizationProvider>
                      {errors.firstOccDate && (
                        <Typography variant="caption" color="error">
                          {errors.firstOccDate}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <FormLabel
                      required
                        sx={{
                          fontSize: "13px",
                          color: "text.primary",
                          fontWeight: 400,
                          marginBottom: "4px",
                        }}
                      >
                        Day of Week
                        <Tooltip title="All recurring Jobs in this series will fall on the selected day of week. You can change each Job's date/day from its individual Job Details page later.">
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: "16px",
                              color: "text.secondary",
                              cursor: "pointer",
                              verticalAlign: "middle"
                            }}
                          />
                        </Tooltip>
                      </FormLabel>
                      <br/>
                      <ToggleButtonGroup
                        value={selectedDaysOfWeek}
                        onChange={handleDayOfWeekChange}
                      >
                        <ToggleButton value="mon">Mon</ToggleButton>
                        <ToggleButton value="tue">Tue</ToggleButton>
                        <ToggleButton value="wed">Wed</ToggleButton>
                        <ToggleButton value="thurs">Thu</ToggleButton>
                        <ToggleButton value="fri">Fri</ToggleButton>
                        <ToggleButton 
                        // disabled 
                        value="sat">
                          Sat
                        </ToggleButton>
                        <ToggleButton
                        //  disabled
                         value="sun">
                          Sun
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {errors.selectedDaysOfWeek && (
                        <Typography variant="caption" color="error">
                          {errors.selectedDaysOfWeek}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6.1}>
                      <FormLabel
                        required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Occurrences
                      </FormLabel>
                      <CustomTextField
                        fullWidth
                        required
                        type="number"
                        value={occurrences}
                        onChange={(e) => {
                          setOccurrences(e.target.value);
                          if (errors.occurrences) setErrors({ occurrences: "" });
                        }}
                      />
                      {errors.occurrences && (
                        <Typography variant="caption" color="error">
                          {errors.occurrences}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6.1}>
                      <FormLabel
                        required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Until
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={lastOccDate}
                          // disablePast
                          minDate={firstOccDate || dayjs()}
                          onChange={handleLastOccDateChange}
                          sx={{ width: "100%" }}
                          slotProps={{
                            textField: {
                              size: "small",
                              helperText: errors.lastOccDate,
                              error: !!errors.lastOccDate,
                            },
                          }}
                        />
                      </LocalizationProvider>
                      {errors.lastOccDate && (
                        <Typography variant="caption" color="error">
                          {errors.lastOccDate}
                        </Typography>
                      )}
                      {calculatedLastOccDate && (
                        <Card
                          sx={{
                            border: `1px solid #23A26D20`,
                            backgroundColor: `#23A26D08`,
                            borderRadius: 2,
                            mt: 1.5,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: `0 4px 12px #23A26D20`,
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                color: '#23A26D',
                                fontWeight: 600,
                                mb: 1,
                                fontSize: '0.875rem'
                              }}
                            >
                              {dayjs(calculatedLastOccDate).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                lineHeight: 1.6,
                                fontSize: "0.8125rem",
                              }}
                            >
                              Last occurrence date calculated automatically based on schedule configuration.
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>
                    {/* DOW */}
                    <Grid item xs={6}>
                      <FormLabel
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Start Time
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileTimePicker
                          value={startTime}
                          onChange={setStartTime}
                          sx={{ width: "100%" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </LocalizationProvider>
                      {errors.startTime && (
                        <Typography variant="caption" color="error">
                          {errors.startTime}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <FormLabel
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        End Time
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileTimePicker
                          value={endTime}
                          onChange={setEndTime}
                          sx={{ width: "100%" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </LocalizationProvider>
                      {errors.endTime && (
                        <Typography variant="caption" color="error">
                          {errors.endTime}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={excludeHolidays}
                            onChange={(e) =>
                              setExcludeHolidays(e.target.checked)
                            }
                            color="success"
                          />
                        }
                        label="Exclude Holidays"
                      />
                    </Grid>
                  </>
                )}

                {/* Monthly */}
                {recurringType === "monthly" && (
                  <>
                    <Grid item xs={12}>
                      <FormLabel
                        sx={{
                          color: "rgba(47, 43, 61, 0.78)!important",
                          marginBottom: "4px",
                          fontSize: "13px",
                        }}
                      >
                        Monthly Options
                      </FormLabel>
                      <RadioGroup
                        row
                        name="monthlyOptions"
                        value={monthlyOptions}
                        onChange={(e) => setMonthlyOptions(e.target.value)}
                      >
                        {/* <FormControlLabel
                          value="billingDay"
                          control={<Radio color="success" />}
                          label="Billing Day Of The Month"
                        /> */}
                        <FormControlLabel
                          value="weekOfMonth"
                          control={<Radio color="success" />}
                          label="Week of the Month"
                        />
                      </RadioGroup>
                    </Grid>

                    {/* {monthlyOptions === "billingDay" && (
                      <Grid item xs={12} md={6.1}>
                        <FormLabel
                        required
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            marginBottom: "4px",
                            color: "text.primary",
                          }}
                        >
                          Day Selector
                        </FormLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            value={firstOccDate}
                            onChange={setFirstOccDate}
                            sx={{ width: "100%" }}
                            slotProps={{ textField: { size: "small" } }}
                          />
                        </LocalizationProvider>
                        {errors.firstOccDate && (
                          <Typography variant="caption" color="error">
                            {errors.firstOccDate}
                          </Typography>
                        )}
                      </Grid>
                    )} */}

                    {monthlyOptions === "weekOfMonth" && (
                      <>
                        <Grid item xs={12} md={6.1}>
                          <FormLabel
                            required
                            sx={{
                              fontWeight: 400,
                              fontSize: "13px",
                              marginBottom: "4px",
                              color: "text.primary",
                              "& .MuiFormLabel-asterisk": {
                                color: "error.main",
                              },
                            }}
                          >
                            First Occurrence Date
                          </FormLabel>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              value={firstOccDate}
                              onChange={handleFirstOccDateChange}
                              sx={{ width: "100%" }}
                              disablePast
                              minDate={dayjs()}
                              slotProps={{ textField: { size: "small" } }}
                            />
                          </LocalizationProvider>
                          {errors.firstOccDate && (
                            <Typography variant="caption" color="error">
                              {errors.firstOccDate}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel
                          required
                            sx={{
                              fontSize: "13px",
                              color: "text.primary",
                              fontWeight: 400,
                              marginBottom: "4px",
                            }}
                          >
                            Day of Week
                            <Tooltip title="All recurring Jobs in this series will fall on the selected day of week. You can change each Job's date/day from its individual Job Details page later.">
                              <InfoOutlinedIcon
                                sx={{
                                  fontSize: "16px",
                                  color: "text.secondary",
                                  cursor: "pointer",
                                  verticalAlign: "middle"
                                }}
                              />
                            </Tooltip>
                          </FormLabel>
                          <br/>
                          <ToggleButtonGroup
                            value={selectedDaysOfWeek}
                            onChange={handleDayOfWeekChange}
                          >
                            <ToggleButton value="mon">Mon</ToggleButton>
                            <ToggleButton value="tue">Tue</ToggleButton>
                            <ToggleButton value="wed">Wed</ToggleButton>
                            <ToggleButton value="thurs">Thu</ToggleButton>
                            <ToggleButton value="fri">Fri</ToggleButton>
                            <ToggleButton 
                            // disabled
                             value="sat">
                              Sat
                            </ToggleButton>
                            <ToggleButton
                            //  disabled 
                             value="sun">
                              Sun
                            </ToggleButton>
                          </ToggleButtonGroup>
                          {errors.selectedDaysOfWeek && (
                            <Typography variant="caption" color="error">
                              {errors.selectedDaysOfWeek}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel
                          required
                            sx={{
                              fontSize: "13px",
                              color: "text.primary",
                              fontWeight: 400,
                              marginBottom: "4px",
                            }}
                          >
                            Week of the Month
                          </FormLabel>
                          <br/>
                          <ToggleButtonGroup
                            value={selectedWeeksOfMonth}
                            onChange={(_, newWeeks) => {
                              if (newWeeks.length > 0) {
                                // only keep the one they just clicked
                                setSelectedWeeksOfMonth([newWeeks[newWeeks.length - 1]]);
                              } 
                            }}
                          >
                            <ToggleButton value="first">1st</ToggleButton>
                            <ToggleButton value="second">2nd</ToggleButton>
                            <ToggleButton value="third">3rd</ToggleButton>
                            <ToggleButton value="fourth">4th</ToggleButton>
                          </ToggleButtonGroup>
                          <br/>
                          {errors.selectedWeeksOfMonth && (
                            <Typography variant="caption" color="error">
                              {errors.selectedWeeksOfMonth}
                            </Typography>
                          )}
                        </Grid>
                        {/* DOW */}
                      </>
                    )}
                     <Grid item xs={12} md={6.1}>
                      <FormLabel
                      required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Occurrences
                      </FormLabel>
                      <CustomTextField
                      fullWidth
                      type="number"
                      value={occurrences}
                      onChange={(e) => {
                        setOccurrences(e.target.value);
                        if (errors.occurrences) setErrors({ occurrences: "" });
                      }}
                    />
                    {errors.occurrences && (
                      <Typography variant="caption" color="error">
                        {errors.occurrences}
                      </Typography>
                    )}
                    </Grid>
                    <Grid item xs={12} md={6.1}>
                      <FormLabel
                        required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Until
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={lastOccDate}
                          disablePast
                          minDate={firstOccDate || dayjs()}
                          onChange={handleLastOccDateChange}
                          sx={{ width: "100%" }}
                          slotProps={{
                            textField: {
                              size: "small",
                              helperText: errors.lastOccDate,
                              error: !!errors.lastOccDate,
                            },
                          }}
                        />
                      </LocalizationProvider>
                      {errors.lastOccDate && (
                        <Typography variant="caption" color="error">
                          {errors.lastOccDate}
                        </Typography>
                      )}
                      {calculatedLastOccDate && (
                        <Card
                          sx={{
                            border: `1px solid #23A26D20`,
                            backgroundColor: `#23A26D08`,
                            borderRadius: 2,
                            mt: 1.5,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: `0 4px 12px #23A26D20`,
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                color: '#23A26D',
                                fontWeight: 600,
                                mb: 1,
                                fontSize: '0.875rem'
                              }}
                            >
                              {dayjs(calculatedLastOccDate).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                lineHeight: 1.6,
                                fontSize: "0.8125rem",
                              }}
                            >
                              Last occurrence date calculated automatically based on schedule configuration.
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>

                    <Grid item xs={6}>
                      <FormLabel
                      required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Start Time
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileTimePicker
                          value={startTime}
                          onChange={setStartTime}
                          sx={{ width: "100%" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </LocalizationProvider>
                      {errors.startTime && (
                        <Typography variant="caption" color="error">
                          {errors.startTime}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <FormLabel
                      required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        End Time
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileTimePicker
                          value={endTime}
                          onChange={setEndTime}
                          sx={{ width: "100%" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </LocalizationProvider>
                      {errors.endTime && (
                        <Typography variant="caption" color="error">
                          {errors.endTime}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={excludeWeekends}
                            onChange={(e) =>
                              setExcludeWeekends(e.target.checked)
                            }
                            color="success"
                          />
                        }
                        label="Exclude Weekends"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={excludeHolidays}
                            onChange={(e) =>
                              setExcludeHolidays(e.target.checked)
                            }
                            color="success"
                          />
                        }
                        label="Exclude Holidays"
                      />
                    </Grid>
                  </>
                )}

                {/* Interval */}
                {recurringType === "Interval-Based" && (
                  <>
                    <Grid item xs={12} md={6.1}>
                      <FormLabel
                        required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                          "& .MuiFormLabel-asterisk": {
                            color: "error.main",
                          },
                        }}
                      >
                        First Occurrence Date
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={firstOccDate}
                          onChange={setFirstOccDate}
                          sx={{ width: "100%" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </LocalizationProvider>
                      {errors.firstOccDate && (
                        <Typography variant="caption" color="error">
                          {errors.firstOccDate}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6.1}>
                      <FormLabel
                      required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Occurrences
                      </FormLabel>
                      <CustomTextField
                      fullWidth
                      type="number"
                      value={occurrences}
                      onChange={(e) => {
                        setOccurrences(e.target.value);
                        if (errors.occurrences) setErrors({ occurrences: "" });
                      }}
                    />
                    {errors.occurrences && (
                      <Typography variant="caption" color="error">
                        {errors.occurrences}
                      </Typography>
                    )}
                    </Grid>
                    <Grid item xs={12} md={6.1}>
                      <FormLabel
                        required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Until
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={lastOccDate}
                          disablePast
                          minDate={firstOccDate || dayjs()}
                          onChange={handleLastOccDateChange}
                          sx={{ width: "100%" }}
                          slotProps={{
                            textField: {
                              size: "small",
                              helperText: errors.lastOccDate,
                              error: !!errors.lastOccDate,
                            },
                          }}
                        />
                      </LocalizationProvider>
                      {errors.lastOccDate && (
                        <Typography variant="caption" color="error">
                          {errors.lastOccDate}
                        </Typography>
                      )}
                      {calculatedLastOccDate && (
                        <Card
                          sx={{
                            border: `1px solid #23A26D20`,
                            backgroundColor: `#23A26D08`,
                            borderRadius: 2,
                            mt: 1.5,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: `0 4px 12px #23A26D20`,
                            },
                          }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                color: '#23A26D',
                                fontWeight: 600,
                                mb: 1,
                                fontSize: '0.875rem'
                              }}
                            >
                              {dayjs(calculatedLastOccDate).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                lineHeight: 1.6,
                                fontSize: "0.8125rem",
                              }}
                            >
                              Last occurrence date calculated automatically based on schedule configuration.
                            </Typography>
                          </CardContent>
                        </Card>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6.1}>
                      <FormLabel
                      required
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Interval (Days)
                      </FormLabel>
                      <CustomTextField
                        fullWidth
                        type="number"
                        value={intervalDays}
                        onChange={(e) => setIntervalDays(e.target.value)}
                      />
                      {errors.intervalDays && (
                        <Typography variant="caption" color="error">
                          {errors.intervalDays}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <FormLabel
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        Start Time
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileTimePicker
                          value={startTime}
                          onChange={setStartTime}
                          sx={{ width: "100%" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </LocalizationProvider>
                      {errors.startTime && (
                        <Typography variant="caption" color="error">
                          {errors.startTime}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <FormLabel
                        sx={{
                          fontWeight: 400,
                          fontSize: "13px",
                          marginBottom: "4px",
                          color: "text.primary",
                        }}
                      >
                        End Time
                      </FormLabel>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileTimePicker
                          value={endTime}
                          onChange={setEndTime}
                          sx={{ width: "100%" }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </LocalizationProvider>
                      {errors.endTime && (
                        <Typography variant="caption" color="error">
                          {errors.endTime}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={excludeWeekends}
                            onChange={(e) =>
                              setExcludeWeekends(e.target.checked)
                            }
                            color="success"
                          />
                        }
                        label="Exclude Weekends"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={excludeHolidays}
                            onChange={(e) =>
                              setExcludeHolidays(e.target.checked)
                            }
                            color="success"
                          />
                        }
                        label="Exclude Holidays"
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
        >
          
          <Button
            variant="tonal"
            color="info"
            onClick={onDialogClose}
            disabled={false}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="main"
            onClick={handleSchedule}
            disabled={isScheduling}
            startIcon={isScheduling ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isScheduling ? "" : "Schedule"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default JobSchedule;
